// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { api } from '@/api/axios-instance';
import {
  reportMutationError,
  reportRequestError,
} from '@/api/report-request-error';
import { AppNotFound } from '@/components/app-not-found';
import { SplashScreen } from '@/components/splash-screen';
import { GooglePlacesProvider } from '@/contexts/google-places-provider';
import { SupportErrorProvider } from '@/contexts/support-error-provider';
import '@/instruments';
import { routeTree } from '@/routeTree.gen';
import { baseThemeOptions } from '@/theme/base-options';
import { lightThemeOptions } from '@/theme/light-options';
import {
  CssBaseline,
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { LicenseInfo } from '@mui/x-license';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import axios, { AxiosError } from 'axios';
import { Settings as LuxonSettings } from 'luxon';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_LICENSE_KEY);

LuxonSettings.throwOnInvalid = true;

// Force Sunday as the first day of the week across every luxon DateTime in
// the app. MUI's AdapterLuxon reads start-of-week via `useLocaleWeeks`,
// which only returns Sunday for en-US when `Intl.Locale.weekInfo` is
// available in the browser — older engines fall back to ISO (Monday).
// Setting defaultWeekSettings pins the behavior regardless of runtime.
LuxonSettings.defaultWeekSettings = {
  firstDay: 7,
  minimalDays: 1,
  weekend: [6, 7],
};

declare module 'luxon' {
  interface TSSettings {
    throwOnInvalid: true;
  }
}

const theme = responsiveFontSizes(
  createTheme(baseThemeOptions, lightThemeOptions)
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Queries intentionally become stale immediately and refetch on tab focus; dropdown reference data uses the shared profile instead
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (failureCount >= 3) {
          return false;
        }
        // Only retry on network failures (no response) or 408 Request Timeout
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          // No response = network failure, retry
          if (status === undefined) {
            return true;
          }
          // 408 = server timeout, retry
          return status === 408;
        }
        // Non-Axios errors (e.g., network errors) - retry
        return true;
      },
    },
  },

  queryCache: new QueryCache({
    onError: (error) => {
      reportRequestError(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reportMutationError(error, mutation);
    },
  }),
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultNotFoundComponent: AppNotFound,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Setup interceptors for handling 401s and 403s
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error instanceof AxiosError) {
      // Handle an unauthenticated request (HTTP 401 Unauthorized) - session expired/invalid
      if (error.response?.status === 401) {
        Sentry.setContext('authentication', {
          authenticated: false,
          lastError: error.response?.data,
        });

        const redirectURL = `${window.location.pathname}${window.location.search}`;
        // Temporary workaround for the infinite redirect stacking
        if (window.location.pathname != '/login') {
          router.navigate({ to: '/login', search: { redirect: redirectURL } });
        }

        return Promise.reject(error);
      }

      // Handle an unauthorized request (HTTP 403 Forbidden) - authenticated but lacks permissions
      if (error.response?.status === 403) {
        // Invalidate current user query as permissions may have changed
        queryClient.invalidateQueries({ queryKey: ['current-user'] });

        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

const rootElement = document.getElementById('root');

if (rootElement === null) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <CssBaseline />
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en-US">
        <GooglePlacesProvider>
          <SupportErrorProvider>
            <QueryClientProvider client={queryClient}>
              <Suspense fallback={<SplashScreen />}>
                <RouterProvider router={router} context={{ queryClient }} />
              </Suspense>
            </QueryClientProvider>
          </SupportErrorProvider>
        </GooglePlacesProvider>
      </LocalizationProvider>
    </ThemeProvider>
    <Analytics />
    <SpeedInsights />
  </StrictMode>
);

// Hide the HTML loading shell now that React has mounted
const loadingShell = document.getElementById('loading-shell');
if (loadingShell) {
  loadingShell.style.display = 'none';
}
